/**
 * normalizeGuestName: the one place guest names are tidied.
 *
 * Pure and idempotent (running it twice changes nothing). This is the dashboard copy of
 * kalligo-api lib/guestName.ts: the API tidies every name it stores, this copy lets the guest form and the
 * import preview show the result before saving. Both run the same fixture table
 * (guestNames.fixture.json) and guestName.crosscheck.test.ts fails if they drift apart.
 *
 * Rules (Oce, 2026-10):
 *  - every word starts with a capital letter ('Raphael chukwudi Nweze' -> 'Raphael Chukwudi Nweze')
 *  - ALL-CAPS words of up to 4 letters are kept (acronyms and ranks: AVM, NNPP, WOSA, BO, OTU);
 *    ALL-CAPS words of 5+ letters become Title Case ('OLUWAMUYIWA' -> 'Oluwamuyiwa')
 *  - mixed-case words are kept (McDonald, O'Brien, Fg, Offr, Rtd); a mixed word that starts
 *    with a lowercase letter only gets its first letter raised ('mcDonald' -> 'McDonald')
 *  - titles are written without a full stop ('Mr.' -> 'Mr', 'Rev.Etim' -> 'Rev Etim')
 *  - runs of whitespace become one space, ends are trimmed
 *  - after an apostrophe a one or two letter ending stays lowercase ("Oko's", "can't")
 *  - anything containing a digit or symbol only ('+1', '+ 1', 'Table 3') is left alone
 *  - an empty or whitespace-only name, null and undefined are returned untouched
 */

// Written without the full stop. Matched case-insensitively.
const TITLES = [
  'mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'rev', 'revd', 'fr', 'pst', 'pastor', 'apst', 'evang',
  'barr', 'engr', 'arc', 'arch', 'hon', 'sen', 'gov', 'amb', 'alh', 'alhj', 'hajia', 'mal', 'mallam',
  'chief', 'sir', 'capt', 'col', 'maj', 'gen', 'lt', 'cpl', 'sgt', 'cdr', 'cmdr', 'rtd', 'dcn', 'msgr', 'bro', 'sis',
  'eng', 'ven', 'snr', 'jnr', 'sr', 'jr',
];

const TITLE_SET = new Set(TITLES);
// A word followed by one or more full stops. No lookbehind, so older Safari can load the dashboard copy.
const WORD_THEN_DOTS = /([\p{L}\p{M}\p{N}'’]+)(\.+)/gu;

// \p{M}: combining marks (decomposed Yoruba and French letters) stay inside their word.
const WORD = /[\p{L}\p{M}\p{N}'’-]+/gu;
const SHORT_ENDING_AFTER_APOSTROPHE = /^(s|t|d|m|ll|re|ve)$/i;

const upperFirstLetter = (s: string): string => s.replace(/\p{L}/u, (c) => c.toUpperCase());

/** Lower-case word -> capitalised, also after an apostrophe unless it is a short ending ('s, 't). */
function capitalise(lower: string): string {
  const parts = lower.split(/(['’])/);
  let out = '';
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (i === 0) out += upperFirstLetter(part);
    else if (part === "'" || part === '’') out += part;
    else out += SHORT_ENDING_AFTER_APOSTROPHE.test(part) ? part : upperFirstLetter(part);
  }
  return out;
}

const letterCount = (text: string): number => text.replace(/[^\p{L}]/gu, '').length;

function fixedStem(word: string, forceTitle: boolean): string {
  const hasLower = /\p{Ll}/u.test(word);
  const hasUpper = /\p{Lu}/u.test(word);
  if (!hasLower && hasUpper) {
    return letterCount(word) <= 4 && !forceTitle ? word : capitalise(word.toLowerCase());
  }
  if (!hasUpper) return capitalise(word);
  return upperFirstLetter(word.slice(0, 1)) + word.slice(1);
}

function fixPart(part: string, forceTitle: boolean): string {
  if (!/\p{L}/u.test(part)) return part;
  // An all-caps possessive ("OKO'S") keeps its ending lowercase; the stem is judged on its own.
  const possessive = part.match(/^(.+)['’]S$/);
  const stem = possessive ? possessive[1] : part;
  const ending = possessive ? `'s` : '';
  return `${fixedStem(stem, forceTitle)}${ending}`;
}

function fixWord(word: string): string {
  if (/\p{N}/u.test(word)) return word;
  const lead = word.match(/^['’-]+/)?.[0] ?? '';
  const trail = word.slice(lead.length).match(/['’-]+$/)?.[0] ?? '';
  const core = word.slice(lead.length, word.length - trail.length);
  if (!/\p{L}/u.test(core)) return word;

  // A hyphenated all-caps compound is judged as one word: 'ANN-MARIE' -> 'Ann-Marie', 'AB-CD' stays.
  const parts = core.split('-');
  const allCaps = !/\p{Ll}/u.test(core);
  const forceTitle = parts.length > 1 && allCaps && letterCount(core) > 4;
  return `${lead}${parts.map((part) => fixPart(part, forceTitle)).join('-')}${trail}`;
}

function tidyOnce(name: string): string {
  // 'Mr.' -> 'Mr', 'Rev.Etim' -> 'Rev Etim'. Only titles lose their full stop; initials such as 'J.' keep theirs.
  const noTitleDots = name.replace(WORD_THEN_DOTS, (match, word: string, _dots: string, offset: number, whole: string) => {
    if (!TITLE_SET.has(word.toLowerCase())) return match;
    const next = whole.charAt(offset + match.length);
    return /[\p{L}\p{N}(]/u.test(next) ? `${word} ` : word;
  });
  const spaced = noTitleDots.replace(/\s+/g, ' ').trim();
  return spaced.replace(WORD, fixWord);
}

export function normalizeGuestName(name: string): string;
export function normalizeGuestName(name: string | null): string | null;
export function normalizeGuestName(name: string | undefined): string | undefined;
export function normalizeGuestName(name: string | null | undefined): string | null | undefined;
export function normalizeGuestName(name: string | null | undefined): string | null | undefined {
  if (typeof name !== 'string') return name;
  if (name.trim() === '') return name;

  // Raising the first letter of a mixed word ('sNNPP') can leave an all-caps word that the rules then
  // retitle ('Snnpp'), so tidy until nothing changes. That is what makes a second run a no-op.
  let current = name;
  for (let pass = 0; pass < 4; pass++) {
    const next = tidyOnce(current);
    if (next === current) break;
    current = next;
  }
  return current;
}

/**
 * Tidies the `name` of each entry in a nested guest list (plus_ones, rsvp_people) and, for rsvp_people,
 * the `replacement` name. Returns a new array; entries without a string name are passed through as they are.
 */
export function normalizeNamedEntries<T extends { name?: unknown }>(entries: T[]): T[];
export function normalizeNamedEntries<T extends { name?: unknown }>(entries: T[] | null | undefined): T[] | null | undefined;
export function normalizeNamedEntries<T extends { name?: unknown }>(entries: T[] | null | undefined): T[] | null | undefined {
  if (!Array.isArray(entries)) return entries;
  return entries.map((entry) => {
    if (entry === null || typeof entry !== 'object') return entry;
    const next: Record<string, unknown> = { ...entry };
    if (typeof next.name === 'string') next.name = normalizeGuestName(next.name);
    if (typeof next.replacement === 'string') next.replacement = normalizeGuestName(next.replacement);
    return next as T;
  });
}
